/**
 * Shared Components for Template Forms
 * RAI Section, Follow-ups Section, and common utilities
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2, 
  ChevronDown, ChevronUp, Users, ListChecks, Building2, Save, Clock,
  Upload, FileDown, FileUp
} from "lucide-react";

// Use relative URLs for production deployment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// ==================== RAI SECTION ====================
export const RAISection = ({ formData, setFormData, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [enabled, setEnabled] = useState(formData.rai_assignments?.length > 0);

  const addRAI = () => {
    setFormData({
      ...formData,
      rai_assignments: [
        ...(formData.rai_assignments || []),
        { task: '', responsible: '', accountable: '', informed: '', due_date: '', status: 'Pending', notes: '' }
      ]
    });
    setEnabled(true);
    setIsOpen(true);
  };

  const updateRAI = (index, field, value) => {
    const items = [...(formData.rai_assignments || [])];
    items[index] = { ...items[index], [field]: value };
    setFormData({ ...formData, rai_assignments: items });
  };

  const removeRAI = (index) => {
    const items = (formData.rai_assignments || []).filter((_, i) => i !== index);
    setFormData({ ...formData, rai_assignments: items });
    if (items.length === 0) setEnabled(false);
  };

  const handleToggle = (checked) => {
    setEnabled(checked);
    if (checked && (!formData.rai_assignments || formData.rai_assignments.length === 0)) {
      addRAI();
    }
    setIsOpen(checked);
  };

  return (
    <div className="border rounded-lg p-4 bg-blue-50/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          <Label className="font-semibold text-blue-900">RAI Assignments</Label>
          <span className="text-xs text-gray-500">(Responsible, Accountable, Informed)</span>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={handleToggle} />
          <span className="text-sm text-gray-500">{enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      </div>

      {enabled && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between mb-2">
              {formData.rai_assignments?.length || 0} assignment(s)
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3">
            {(formData.rai_assignments || []).map((item, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-7 gap-2 p-3 bg-white rounded border">
                <div className="md:col-span-2">
                  <Label className="text-xs">Task/Item</Label>
                  <Input 
                    value={item.task} 
                    onChange={(e) => updateRAI(idx, 'task', e.target.value)} 
                    placeholder="Task description"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-blue-600">Responsible</Label>
                  <Input 
                    value={item.responsible} 
                    onChange={(e) => updateRAI(idx, 'responsible', e.target.value)} 
                    placeholder="Who does it"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-orange-600">Accountable</Label>
                  <Input 
                    value={item.accountable} 
                    onChange={(e) => updateRAI(idx, 'accountable', e.target.value)} 
                    placeholder="Who approves"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-green-600">Informed</Label>
                  <Input 
                    value={item.informed} 
                    onChange={(e) => updateRAI(idx, 'informed', e.target.value)} 
                    placeholder="Who to notify"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Due Date</Label>
                  <Input 
                    type="date"
                    value={item.due_date} 
                    onChange={(e) => updateRAI(idx, 'due_date', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="flex items-end gap-1">
                  <div className="flex-1">
                    <Label className="text-xs">Status</Label>
                    <Select value={item.status} onValueChange={(v) => updateRAI(idx, 'status', v)}>
                      <SelectTrigger className="text-sm h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Blocked">Blocked</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeRAI(idx)} className="h-9 w-9">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addRAI} className="w-full">
              <Plus className="h-4 w-4 mr-2" /> Add RAI Assignment
            </Button>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};

// ==================== FOLLOW-UPS SECTION ====================
export const FollowupsSection = ({ formData, setFormData, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [enabled, setEnabled] = useState(formData.followups?.length > 0);

  const addFollowup = () => {
    setFormData({
      ...formData,
      followups: [
        ...(formData.followups || []),
        { item: '', owner: '', due_date: '', priority: 'Medium', status: 'Pending', dependencies: '', notes: '' }
      ]
    });
    setEnabled(true);
    setIsOpen(true);
  };

  const updateFollowup = (index, field, value) => {
    const items = [...(formData.followups || [])];
    items[index] = { ...items[index], [field]: value };
    setFormData({ ...formData, followups: items });
  };

  const removeFollowup = (index) => {
    const items = (formData.followups || []).filter((_, i) => i !== index);
    setFormData({ ...formData, followups: items });
    if (items.length === 0) setEnabled(false);
  };

  const handleToggle = (checked) => {
    setEnabled(checked);
    if (checked && (!formData.followups || formData.followups.length === 0)) {
      addFollowup();
    }
    setIsOpen(checked);
  };

  return (
    <div className="border rounded-lg p-4 bg-amber-50/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-amber-600" />
          <Label className="font-semibold text-amber-900">Follow-ups & Action Items</Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={handleToggle} />
          <span className="text-sm text-gray-500">{enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      </div>

      {enabled && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between mb-2">
              {formData.followups?.length || 0} item(s)
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3">
            {(formData.followups || []).map((item, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-2 p-3 bg-white rounded border">
                <div className="md:col-span-2">
                  <Label className="text-xs">Follow-up Item</Label>
                  <Input 
                    value={item.item} 
                    onChange={(e) => updateFollowup(idx, 'item', e.target.value)} 
                    placeholder="Action item description"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Owner</Label>
                  <Input 
                    value={item.owner} 
                    onChange={(e) => updateFollowup(idx, 'owner', e.target.value)} 
                    placeholder="Assigned to"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Due Date</Label>
                  <Input 
                    type="date"
                    value={item.due_date} 
                    onChange={(e) => updateFollowup(idx, 'due_date', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={item.priority} onValueChange={(v) => updateFollowup(idx, 'priority', v)}>
                    <SelectTrigger className="text-sm h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-1">
                  <div className="flex-1">
                    <Label className="text-xs">Status</Label>
                    <Select value={item.status} onValueChange={(v) => updateFollowup(idx, 'status', v)}>
                      <SelectTrigger className="text-sm h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Deferred">Deferred</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeFollowup(idx)} className="h-9 w-9">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addFollowup} className="w-full">
              <Plus className="h-4 w-4 mr-2" /> Add Follow-up
            </Button>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};

// ==================== TEMPLATE HEADER ====================
export const TemplateHeader = ({ 
  title, 
  description, 
  onSample, 
  onReset, 
  autoSave, 
  setAutoSave, 
  lastSaved,
  showAutoSave = true 
}) => {
  const navigate = useNavigate();
  
  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-10">
      <div className="max-w-5xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{title}</h1>
              <p className="text-sm text-gray-500">{description}</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-end">
            {showAutoSave && setAutoSave && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
            )}
            {onSample && (
              <Button variant="outline" size="sm" onClick={onSample} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample Data
              </Button>
            )}
            {onReset && (
              <Button variant="outline" size="sm" onClick={onReset} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            )}
          </div>
        </div>
        {lastSaved && (
          <div className="text-xs text-gray-500 mt-2 text-right">
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>
    </header>
  );
};

// ==================== DOWNLOAD BUTTON WITH SAVE DRAFT ====================
export const DownloadButton = ({ 
  loading, 
  disabled, 
  format, 
  onClick,
  onSaveDraft,
  saving,
  savedTemplateId 
}) => (
  <div className="flex gap-3">
    {onSaveDraft && (
      <Button 
        onClick={onSaveDraft} 
        disabled={saving} 
        variant="outline"
        className="flex-1" 
        size="lg"
        data-testid="save-draft-btn"
      >
        <Save className="h-5 w-5 mr-2" /> 
        {saving ? "Saving..." : (savedTemplateId ? "Update Draft" : "Save Draft")}
      </Button>
    )}
    <Button 
      onClick={onClick} 
      disabled={loading || disabled} 
      className={onSaveDraft ? "flex-1" : "w-full"} 
      size="lg"
      data-testid="download-btn"
    >
      <Download className="h-5 w-5 mr-2" /> 
      {loading ? "Generating..." : `Download ${format}`}
    </Button>
  </div>
);

// ==================== SAVE DRAFT HOOK (FIXED) ====================
/**
 * Custom hook for save/auto-save functionality
 * Uses refs to avoid stale closure issues with intervals
 */
export const useSaveDraft = (templateId, templateType, format = 'DOCX', userEmail = '') => {
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const [searchParams] = useSearchParams();
  const editingSavedId = searchParams.get('saved');
  
  // Use refs to always have access to latest values in interval callback
  const savedTemplateIdRef = useRef(savedTemplateId);
  const userEmailRef = useRef(userEmail);
  
  // Keep refs in sync with state
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);
  
  useEffect(() => {
    userEmailRef.current = userEmail;
  }, [userEmail]);

  // Load saved template if editing
  const loadSavedTemplate = async (setFormData) => {
    if (editingSavedId) {
      try {
        const headers = userEmail ? { 'X-User-Email': userEmail } : {};
        const response = await axios.get(`${API}/saved-templates/${editingSavedId}`, { headers });
        if (response.data && response.data.data) {
          setFormData(response.data.data);
          setSavedTemplateId(editingSavedId);
        }
      } catch (error) {
        console.error("Error loading saved template:", error);
      }
    }
  };

  // Core save function that uses refs for latest values
  const saveDraftCore = async (formData, isAutoSave = false) => {
    const currentSavedTemplateId = savedTemplateIdRef.current;
    const currentUserEmail = userEmailRef.current;
    const headers = currentUserEmail ? { 'X-User-Email': currentUserEmail } : {};
    
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = formData.title || formData.project_name || formData.meeting_title || 
                       formData.company_name || `${templateType} Draft`;
      
      // Auto-increment version for manual saves (not auto-save)
      let version = formData.version || '1.0';
      if (currentSavedTemplateId && !isAutoSave) {
        const vNum = parseFloat(version) || 1.0;
        version = (vNum + 0.1).toFixed(1);
      }
      
      const payload = {
        name: draftName,
        template_type: templateType,
        template_id: templateId,
        format: format,
        data: { ...formData, version }
      };
      
      if (currentSavedTemplateId) {
        await axios.put(`${API}/saved-templates/${currentSavedTemplateId}`, payload, { headers });
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return { success: true, version };
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return { success: false, error };
    }
  };

  // Public save function with alert
  const handleSaveDraft = async (formData, isAutoSave = false) => {
    const result = await saveDraftCore(formData, isAutoSave);
    if (!isAutoSave) {
      if (result.success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
    return result;
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, []);

  return {
    saving,
    autoSave,
    setAutoSave,
    lastSaved,
    savedTemplateId,
    setSavedTemplateId,
    editingSavedId,
    loadSavedTemplate,
    handleSaveDraft,
    saveDraftCore,
    autoSaveIntervalRef
  };
};

// ==================== AUTO-SAVE TOGGLE ====================
export const AutoSaveToggle = ({ autoSave, setAutoSave, templateName }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
    <div className="flex items-center gap-2">
      <Building2 className="h-4 w-4 text-gray-500" />
      <div>
        <Label className="text-sm font-medium">Auto-save for editing</Label>
        <p className="text-xs text-gray-500">Save this document to &quot;Saved Documents&quot; for later editing</p>
      </div>
    </div>
    <Switch checked={autoSave} onCheckedChange={setAutoSave} />
  </div>
);

// ==================== SAVE TEMPLATE HANDLER ====================
export const saveTemplateForEditing = async (templateId, templateName, formData, token) => {
  try {
    await axios.post(`${API}/saved-templates?token=${token}`, {
      template_id: templateId,
      name: templateName || `${templateId} - ${new Date().toLocaleDateString()}`,
      form_data: formData
    });
    return true;
  } catch (error) {
    console.error("Error saving template:", error);
    return false;
  }
};

// ==================== DYNAMIC LIST ====================
export const DynamicList = ({ items, setItems, placeholder, label }) => {
  const addItem = () => setItems([...items, '']);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));
  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2">
          <Input 
            value={item} 
            onChange={(e) => updateItem(idx, e.target.value)} 
            placeholder={placeholder} 
          />
          <Button variant="ghost" size="icon" onClick={() => removeItem(idx)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addItem}>
        <Plus className="h-4 w-4 mr-2" /> Add Item
      </Button>
    </div>
  );
};

// ==================== UTILITY: Download Handler ====================
export const handleDownload = async (endpoint, formData, filename, format, setLoading) => {
  setLoading(true);
  try {
    const response = await axios.post(`${API}/generate/${endpoint}`, formData, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    const ext = format.toLowerCase() === 'xlsx' ? 'xlsx' : 'docx';
    link.setAttribute("download", `${filename}.${ext}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error:", error);
    alert("Error generating document. Please check required fields.");
  }
  setLoading(false);
};

// ==================== DCTM FILE UTILITIES ====================
/**
 * .dctm (Document Template) file format
 * A JSON-based portable format for document templates with metadata
 */
export const DCTM_FILE_VERSION = "1.0";

/**
 * Export form data to .dctm file
 * @param {Object} formData - The form data to export
 * @param {string} templateId - The template identifier
 * @param {string} templateType - Human readable template type
 * @param {string} createdBy - Email of the creator
 * @param {string} fileName - Optional custom filename
 */
export const exportToDCTM = (formData, templateId, templateType, createdBy = '', fileName = '') => {
  // Build the DCTM document
  const dctmDocument = {
    // File format metadata
    _dctm: {
      version: DCTM_FILE_VERSION,
      format: "dctm",
      generator: "Document Templates",
      exportedAt: new Date().toISOString()
    },
    // Template metadata
    meta: {
      templateId,
      templateType,
      createdBy,
      documentVersion: formData.version || "1.0",
      name: formData.project_name || formData.title || formData.meeting_title || formData.company_name || `${templateType} Document`
    },
    // The actual form data
    data: formData
  };

  // Create the JSON string with pretty formatting
  const jsonString = JSON.stringify(dctmDocument, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  // Generate filename
  const docName = fileName || dctmDocument.meta.name.replace(/[^a-zA-Z0-9]/g, '_');
  const finalFileName = `${docName}_v${dctmDocument.meta.documentVersion}.dctm`;
  
  // Trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = finalFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  return true;
};

/**
 * Import and parse a .dctm file
 * @param {File} file - The file object to import
 * @returns {Promise<{success: boolean, data?: Object, meta?: Object, error?: string}>}
 */
export const importFromDCTM = (file) => {
  return new Promise((resolve) => {
    if (!file) {
      resolve({ success: false, error: 'No file provided' });
      return;
    }
    
    // Check file extension
    if (!file.name.toLowerCase().endsWith('.dctm')) {
      resolve({ success: false, error: 'Invalid file format. Please select a .dctm file.' });
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const parsed = JSON.parse(content);
        
        // Validate DCTM structure
        if (!parsed._dctm || !parsed.meta || !parsed.data) {
          resolve({ success: false, error: 'Invalid .dctm file structure. Missing required sections.' });
          return;
        }
        
        resolve({
          success: true,
          data: parsed.data,
          meta: parsed.meta,
          dctmInfo: parsed._dctm
        });
      } catch (err) {
        resolve({ success: false, error: 'Failed to parse .dctm file. The file may be corrupted.' });
      }
    };
    
    reader.onerror = () => {
      resolve({ success: false, error: 'Failed to read the file.' });
    };
    
    reader.readAsText(file);
  });
};

/**
 * Validate if imported data matches the expected template
 * @param {string} expectedTemplateId - The template ID to validate against
 * @param {Object} importedMeta - The meta object from the imported file
 * @returns {{valid: boolean, warning?: string}}
 */
export const validateDCTMTemplate = (expectedTemplateId, importedMeta) => {
  if (!importedMeta || !importedMeta.templateId) {
    return { valid: false, warning: 'Missing template information in file.' };
  }
  
  if (importedMeta.templateId !== expectedTemplateId) {
    return { 
      valid: true, // Allow import but warn
      warning: `This file was created for "${importedMeta.templateType}" template. Some fields may not match.`
    };
  }
  
  return { valid: true };
};

/**
 * Hook for DCTM import/export functionality
 */
export const useDCTMFile = (templateId, templateType, userEmail = '') => {
  const fileInputRef = useRef(null);
  
  const handleExport = (formData, customFileName = '') => {
    return exportToDCTM(formData, templateId, templateType, userEmail, customFileName);
  };
  
  const handleImport = async (file, setFormData, setSavedTemplateId) => {
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return false;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate(templateId, result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return false;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    
    // Reset saved template ID since this is a new import
    if (setSavedTemplateId) {
      setSavedTemplateId(null);
    }
    
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
    return true;
  };
  
  const triggerImport = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  return {
    fileInputRef,
    handleExport,
    handleImport,
    triggerImport
  };
};

/**
 * DCTM Import/Export Buttons Component
 * Use this for consistent button styling across all templates
 */
export const DCTMButtons = ({ 
  onExport, 
  onImportClick, 
  fileInputRef, 
  onFileSelect,
  disabled = false,
  variant = "outline"
}) => (
  <>
    <input
      type="file"
      ref={fileInputRef}
      accept=".dctm"
      onChange={(e) => {
        if (e.target.files?.[0]) {
          onFileSelect(e.target.files[0]);
          e.target.value = ''; // Reset for re-selection
        }
      }}
      className="hidden"
      data-testid="dctm-file-input"
    />
    <Button 
      onClick={onImportClick} 
      disabled={disabled} 
      variant={variant}
      size="lg"
      className="flex-1"
      data-testid="import-dctm-btn"
    >
      <FileUp className="h-5 w-5 mr-2" /> Import .dctm
    </Button>
    <Button 
      onClick={onExport} 
      disabled={disabled} 
      variant={variant}
      size="lg"
      className="flex-1"
      data-testid="export-dctm-btn"
    >
      <FileDown className="h-5 w-5 mr-2" /> Export .dctm
    </Button>
  </>
);

export { Card, CardContent, CardHeader, CardTitle, Input, Label, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Separator, Button, Plus, Trash2, Save, Clock, FileUp, FileDown };
